import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from '@/components/ui/toast';
import { SortingState } from '@tanstack/react-table';
import UserGridDisplay from './components/UserGridDisplay';
import { SearchAndFilters } from './components/SearchAndFilters';
import { LIST_USERS, RESET_PASSWORD, MODIFY_USER, CREATE_USER_ACCOUNT, LIST_MANAGERS } from './queries';
import {
  FilterState,
  FilterVariables,
  UserUpdateData,
  ListUsersResponse,
  UserPermission,
  User,
  ManagerOption
} from './types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { validatePasswordSimple } from '@/utils/passwordValidation';
import { CreateUserDialog, CreateUserDialogValues } from './components/CreateUserDialog';

const USERS_PER_PAGE = 20;
const FILTER_STORAGE_KEY = 'user-accounts-filters';

const defaultFilterState: FilterState = {
  showDisabled: true,
  showLocked: true,
  twoFactorStatus: '',
  accountStatus: '',
  lockedStatus: '',
  compliant: '',
  sortBy: 'name',
  sortDirection: 'asc'
};

const sanitizeFilterState = (value: unknown): FilterState => {
  const source = value && typeof value === 'object' ? value as Partial<FilterState> : {};
  const sortDirection = source.sortDirection === 'desc' ? 'desc' : 'asc';
  const sortBy = typeof source.sortBy === 'string' && source.sortBy
    ? source.sortBy === 'username' ? 'name' : source.sortBy
    : defaultFilterState.sortBy;

  return {
    showDisabled: typeof source.showDisabled === 'boolean' ? source.showDisabled : defaultFilterState.showDisabled,
    showLocked: typeof source.showLocked === 'boolean' ? source.showLocked : defaultFilterState.showLocked,
    twoFactorStatus: typeof source.twoFactorStatus === 'string' ? source.twoFactorStatus : defaultFilterState.twoFactorStatus,
    accountStatus: typeof source.accountStatus === 'string' ? source.accountStatus : defaultFilterState.accountStatus,
    lockedStatus: typeof source.lockedStatus === 'string' ? source.lockedStatus : defaultFilterState.lockedStatus,
    compliant: typeof source.compliant === 'string' ? source.compliant : defaultFilterState.compliant,
    sortBy,
    sortDirection
  };
};

interface ErrorStateProps {
  error: Error | string;
  title?: string;
  showCard?: boolean;
  onRetry?: () => void;
}

type CreateUserAccountMutationResponse = {
  createUserAccount?: {
    success?: boolean;
    message?: string;
    createdUserId?: number | null;
    username?: string | null;
    temporaryPassword?: string | null;
  };
};

type ListManagersResponse = {
  listManagers: ManagerOption[];
};

const ErrorState: React.FC<ErrorStateProps> = ({ 
  error, 
  title = "Error",
  showCard = false,
  onRetry
}) => {
  const errorMessage = typeof error === 'string' ? error : error.message;
  
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="mt-2">
        {errorMessage}
        {onRetry && (
          <Button
            onClick={onRetry}
            variant="outline"
            size="sm"
            className="mt-3"
          >
            <RefreshCw className="mr-2 h-3 w-3" />
            Retry
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
};
  
export function UserManagement() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(USERS_PER_PAGE);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState<FilterState>(defaultFilterState);
  const [cachedUsers, setCachedUsers] = useState<User[]>([]);
  const [cachedTotalCount, setCachedTotalCount] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored);
      const storedFilters = parsed.filterState ? sanitizeFilterState(parsed.filterState) : null;
      if (storedFilters) storedFilters.showDisabled = true;
      if (storedFilters) setFilterState(storedFilters);
      if (typeof parsed.searchTerm === 'string') setSearchTerm(parsed.searchTerm);
      if (Number.isInteger(parsed.pageIndex)) setPageIndex(parsed.pageIndex);
      if (Number.isInteger(parsed.pageSize)) setPageSize(parsed.pageSize);
      if (Array.isArray(parsed.sorting)) setSorting(parsed.sorting);
    } catch (err) {
      console.error('Failed to load saved user filters', err);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const payload = {
      searchTerm,
      filterState,
      pageIndex,
      pageSize,
      sorting
    };
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(payload));
  }, [searchTerm, filterState, pageIndex, pageSize, sorting]);

  const buildFilters = useCallback((
    options: {
      search?: string;
      limit?: number;
      offset?: number;
      filterOverrides?: Partial<FilterState>;
    } = {}
  ): FilterVariables => {
    const limitValue = options.limit ?? pageSize;
    const offsetValue = options.offset ?? pageIndex * limitValue;
    const searchValue = options.search ?? searchTerm;
    const nextFilterState = { ...filterState, ...(options.filterOverrides || {}) };

    return {
      search: searchValue,
      limit: limitValue,
      offset: offsetValue,
      showDisabled: nextFilterState.showDisabled,
      showLocked: nextFilterState.showLocked,
      twoFactorStatus: nextFilterState.twoFactorStatus,
      accountStatus: nextFilterState.accountStatus,
      lockedStatus: nextFilterState.lockedStatus,
      compliant: nextFilterState.compliant ? nextFilterState.compliant === 'yes' : null,
      sortBy: nextFilterState.sortBy,
      sortDirection: nextFilterState.sortDirection
    };
  }, [filterState, pageIndex, pageSize, searchTerm]);

  const filters = useMemo(() => buildFilters(), [buildFilters]);

  const { loading, error, data, refetch } = useQuery<ListUsersResponse>(LIST_USERS, {
    variables: { filters },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all'
  });
  const { data: managersData, refetch: refetchManagers } = useQuery<ListManagersResponse>(LIST_MANAGERS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });

  useEffect(() => {
    if (!data?.listUsers) return;
    if (isDialogOpen) return;
    setCachedUsers(data.listUsers.entries || []);
    setCachedTotalCount(data.listUsers.totalCount || 0);
  }, [data, isDialogOpen]);

  const refetchWithFilters = useCallback(async (options?: {
    search?: string;
    limit?: number;
    offset?: number;
    filterOverrides?: Partial<FilterState>;
  }) => {
    const filtersToUse = buildFilters(options);
    await refetch({ filters: filtersToUse });
    return filtersToUse;
  }, [buildFilters, refetch]);

  const [resetPasswordMutation] = useMutation(RESET_PASSWORD);
  const [modifyUserMutation] = useMutation(MODIFY_USER);
  const [createUserAccountMutation, { loading: creatingUserAccount }] = useMutation<CreateUserAccountMutationResponse>(CREATE_USER_ACCOUNT);

  const handlePaginationChange = useCallback(async (newPageIndex: number, newPageSize: number) => {
    setPageIndex(newPageIndex);
    setPageSize(newPageSize);
    try {
      await refetchWithFilters({
        limit: newPageSize,
        offset: newPageIndex * newPageSize
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change page';
      console.error('Pagination error:', error);
      toast.error(errorMessage);
    }
  }, [refetchWithFilters]);

  const handleSearch = useCallback(async (searchValue: string) => {
    const normalizedSearch = searchValue.trim();
    setSearchTerm(normalizedSearch);
    setPageIndex(0);
    try {
      await refetchWithFilters({
        search: normalizedSearch,
        offset: 0
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Search failed';
      console.error('Search error:', error);
      toast.error(errorMessage);
    }
  }, [refetchWithFilters]);

  const handleFilterChange = useCallback(async (updates: Partial<FilterState>) => {
    const newFilterState: FilterState = { ...filterState, ...updates };
    setFilterState(newFilterState);
    setPageIndex(0);
    
    try {
      await refetchWithFilters({
        offset: 0,
        filterOverrides: newFilterState
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to apply filters';
      console.error('Filter error:', error);
      toast.error(errorMessage);
    }
  }, [filterState, refetchWithFilters]);

  const handleSortingChange = useCallback(async (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    setSorting(newSorting);
    
    if (newSorting.length > 0) {
      const sortField = newSorting[0].id;
      const sortDir = newSorting[0].desc ? 'desc' : 'asc';


      const newFilterState: FilterState = {
        ...filterState,
        sortBy: sortField,
        sortDirection: sortDir
      };
      setFilterState(newFilterState);

      try {
        await refetchWithFilters({
          filterOverrides: newFilterState
        });
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to sort data';
        console.error('Sort error:', error);
        toast.error(errorMessage);
      }
    }
  }, [sorting, filterState, refetchWithFilters]);


  const validateEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  const validatePassword = useCallback((password: string): string | null => {
    return validatePasswordSimple(password);
  }, []);

  const handleUpdateUser = useCallback(async (username: string, updates: UserUpdateData) => {
    if (!username) {
      toast.error('Username is required');
      return;
    }

    // Validate email if provided
    if (updates.email && !validateEmail(updates.email)) {
      toast.error('Please provide a valid email address');
      return;
    }

    // Validate password if provided
    if (updates.password) {
      const passwordError = validatePassword(updates.password);
      if (passwordError) {
        toast.error(passwordError);
        return;
      }
    }

    try {
      const variables: {
        username: string;
		newUsername?: string;
        disabled?: boolean;
        email?: string;
        realname?: string;
        organization?: string | null;
        site?: string | null;
        password?: string;
        permissions?: { permission: string; enabled: boolean }[];
        twoFactorMethod?: string;
        handlerUsername?: string | null;
        isManager?: boolean;
        deactivationDate?: string | null;
        deactivationReason?: string | null;
      } = {
        username,
		newUsername: updates.username,
        disabled: updates.disabled,
        email: updates.email,
        realname: updates.realname,
        organization: updates.organization,
        site: updates.site,
        password: updates.password,
        twoFactorMethod: updates.twoFactorMethod,
        handlerUsername: updates.handlerUsername,
        isManager: updates.isManager,
        deactivationDate: updates.deactivationDate,
        deactivationReason: updates.deactivationReason,
      };

      if (updates.permissions) {
        variables.permissions = updates.permissions.map((p: UserPermission) => ({
          permission: p.permission,
          enabled: p.enabled,
        }));
      }

	  const response = await modifyUserMutation({ variables });
	  const message = `${response.data?.modifyUser?.message || ''}`.trim();
	  if (!message.startsWith('User updated successfully')) {
		throw new Error(message || 'Failed to update user');
	  }
      await Promise.all([refetchWithFilters(), refetchManagers()]);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user';
      console.error('Update user error:', err);
	  throw new Error(errorMessage);
    }
  }, [modifyUserMutation, refetchManagers, refetchWithFilters, validateEmail, validatePassword]);


  const handleResetPassword = useCallback(async (username: string) => {
    if (!username) {
      toast.error('Username is required');
      return;
    }

    try {
      await resetPasswordMutation({
        variables: { username }
      });
      toast.success('Password setup link sent');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send password setup link';
      console.error('Reset password error:', err);
      toast.error(errorMessage);
    }
  }, [resetPasswordMutation]);

  const handleChangePassword = useCallback(async (username: string, password: string) => {
    if (!username) {
      throw new Error('Username is required');
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      throw new Error(passwordError);
    }

    try {
      await modifyUserMutation({
        variables: { username, password, passwordfailures: 0 }
      });
      await refetchWithFilters();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to set password';
      console.error('Change password error:', err);
      throw new Error(errorMessage);
    }
  }, [modifyUserMutation, refetchWithFilters, validatePassword]);

  const handleUnlockUser = useCallback(async (username: string) => {
    if (!username) {
      toast.error('Username is required');
      return;
    }

    try {
      await modifyUserMutation({
        variables: { username, passwordfailures: 0 }
      });
      toast.success('Password attempts reset');
      await refetchWithFilters();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to unlock user';
      console.error('Unlock user error:', err);
      toast.error(errorMessage);
    }
  }, [modifyUserMutation, refetchWithFilters]);

  const handleCreateUser = useCallback(async (values: CreateUserDialogValues) => {
    try {
      const response = await createUserAccountMutation({
        variables: {
          input: {
            username: values.username,
            realname: values.realname,
            emailAddress: values.emailAddress,
            organization: values.organization || null,
            site: values.site || null,
            twoFactorMethod: values.twoFactorMethod,
            legacyAccesses: values.legacyAccesses,
            selectedPolicies: values.selectedPolicies,
            activateNow: true
          }
        }
      });

      const result = response?.data?.createUserAccount;
      if (!result?.success) {
        toast.error(result?.message || 'Unable to create user');
        return;
      }

      const username = result.username || values.username;
      toast.success(result.message || `User ${username} created.`);
      setIsCreateDialogOpen(false);
      await refetchWithFilters();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create user';
      toast.error(errorMessage);
    }
  }, [createUserAccountMutation, refetchWithFilters]);

  if (error) {
    return (
      <div className="p-6 mx-auto">
        <ErrorState 
          error={error} 
          title="Failed to load users"
          showCard={true}
          onRetry={() => refetchWithFilters()}
        />
      </div>
    );
  }

  return (
    <div className="p-6 mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <SearchAndFilters
          onSearch={handleSearch}
          searchValue={searchTerm}
          filterState={filterState}
          onFilterChange={handleFilterChange}
          totalCount={data?.listUsers?.totalCount || cachedTotalCount}
          filteredCount={(data?.listUsers?.entries || cachedUsers).length}
          loading={loading}
          onCreateUser={() => setIsCreateDialogOpen(true)}
        />

        <div className="mt-6">
          <UserGridDisplay
            users={data?.listUsers?.entries || cachedUsers}
            managerOptions={managersData?.listManagers || []}
            onUpdateUser={handleUpdateUser}
        onResetPassword={handleResetPassword}
        onChangePassword={handleChangePassword}
        onUnlockUser={handleUnlockUser}
        onDialogOpen={() => setIsDialogOpen(true)}
        onDialogClose={() => setIsDialogOpen(false)}
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={handlePaginationChange}
        onSortingChange={handleSortingChange}
        sorting={sorting}
            totalCount={data?.listUsers?.totalCount || cachedTotalCount}
            loading={loading}
          />
        </div>

        <CreateUserDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          onSubmit={handleCreateUser}
          submitting={creatingUserAccount}
          title="Create User Account"
          description="Create a new user account from User Admin."
          submitLabel="Create User Account"
        />
      </div>
    </div>
  );
}


export default UserManagement;
